import { Permission } from '@shared/infrastructure/auth/permission.js';
import type { AppModule } from '@shared/presentation/router/composition.js';
import { VP_SECTION_NAV } from '../presentation/section-nav.js';

/**
 * `analysis-config` feature of Virtual Profiler.
 *
 * HU-37 to HU-41. A configuration is born from «Estándar» or as a copy (a short form over
 * the list) and is changed on its detail screen, tab by tab.
 *
 * The configuration screen is the **active** configuration's detail: «Configuración» reads which
 * one Beon analyses with and opens it on «NEXT Semántica». Everything else — the list of all of
 * them, the versions, the gesture catalogue — is reached from there and slides in over it.
 *
 * Every route is under `virtual-profiler:configure`, the read-only ones too. Not zeal:
 * what is read here are the parameters everything is measured with, and whoever cannot
 * change them does not need to read them either — the analysis screens say which
 * configuration each run used.
 *
 * It renders inside the shell, hence `layoutRoutes`. With no `meta.public` the
 * guard protects it, which is the default.
 */
export const virtualProfilerAnalysisConfigModule: AppModule = {
  name: 'virtual-profiler-analysis-config',
  routes: [],
  layoutRoutes: [
    /**
     * The engine's registry. Read-only, and reached from the configuration screens: a detail of
     * theirs, so it slides in over them.
     */
    {
      path: 'virtual-profiler/configs/gesture-catalogue',
      name: 'virtual-profiler-gesture-catalogue',
      component: () => import('./presentation/GestureCatalogueView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_CONFIGURE,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /**
     * One configuration: its detail screen, with tabs in `?tab=`. The id is digits only, so no
     * static segment beside it — `all`, `gesture-catalogue` — can ever be read as a configuration.
     *
     * The module's main screen, not a detail over a list: it does not slide in. Opened from the
     * nav it just appears, as every section does, and what was opened over it slides away to
     * reveal it.
     */
    {
      path: 'virtual-profiler/configs/:configId(\\d+)',
      name: 'virtual-profiler-config-edit',
      component: () => import('./presentation/AnalysisConfigDetailView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_CONFIGURE,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /** One configuration's versions. It slides in over the configuration it was opened from. */
    {
      path: 'virtual-profiler/configs/:configId/versions',
      name: 'virtual-profiler-config-versions',
      component: () => import('./presentation/ConfigVersionsView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_CONFIGURE,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /**
     * Every configuration, and «Nueva configuración» as a dialog on top of the list.
     *
     * Auxiliary, like the versions: reached from a configuration's header, so it slides in over it.
     * Nested so the form keeps its own address — `?copyOf=` opens it as a copy, which is what
     * «Duplicar» on a detail screen links to — while the list stays mounted behind it. A dialog
     * whose backdrop covers an empty page reads as a broken screen.
     */
    {
      path: 'virtual-profiler/configs/all',
      name: 'virtual-profiler-config-list',
      component: () => import('./presentation/AnalysisConfigsView.vue'),
      // The child — the short form as a dialog over the list — inherits it: meta merges down.
      meta: {
        permission: Permission.VIRTUAL_PROFILER_CONFIGURE,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
      children: [
        {
          path: 'new',
          name: 'virtual-profiler-config-new',
          component: () => import('./presentation/NewConfigDialog.vue'),
          meta: { permission: Permission.VIRTUAL_PROFILER_CONFIGURE },
        },
      ],
    },
    /**
     * «Configuración» (owner, 2026-10-14): the active configuration at its own address, on
     * «Metodología» — `/virtual-profiler/configuration/active?tab=methodology`. It reads which one
     * is active and shows its detail here; the address stays. The name is the one the module's
     * nav and the hub link to, so neither has to know which configuration is active.
     */
    {
      path: 'virtual-profiler/configuration/active',
      name: 'virtual-profiler-analysis-config',
      component: () => import('./presentation/ActiveConfigView.vue'),
      meta: { permission: Permission.VIRTUAL_PROFILER_CONFIGURE, sectionNav: VP_SECTION_NAV },
    },
    /** The old address of «Configuración»: links to it land on the new one, query and all. */
    {
      path: 'virtual-profiler/configs',
      redirect: (to) => ({ name: 'virtual-profiler-analysis-config', query: to.query }),
    },
  ],
};
